import {BaseFields, BaseModel, BaseProps, createFieldDef, FieldDefinition, ModelFieldType} from './baseModel';
import {ModelResponse, ResponseData} from "@dvrd/dvr-controls";
import {NotificationInterval, NotificationType} from "../util/enums";
import {createNotification, deleteNotification, getNotifications, updateNotification} from "../util/requestSender";

interface Props extends BaseProps {
    notification_type?: NotificationType;
    interval?: NotificationInterval;
    email_addresses?: Array<string>;
    last_sent?: string;
    flex?: any | null;
    account_id?: string;
}

export interface NotificationFields extends BaseFields {
    notification_type: NotificationType;
    interval: NotificationInterval;
    email_addresses: Array<string>;
    last_sent: string;
    flex: any | null;
    account_id: string;
}

const fields: NotificationFields = {
    id: '',
    created_at: '',
    updated_at: '',
    notification_type: NotificationType.REVIEWS,
    interval: NotificationInterval.INSTANT,
    email_addresses: [],
    last_sent: '',
    flex: null,
    account_id: '',
};

const fieldsDefinition: { [key in keyof NotificationFields]: FieldDefinition } = {
    id: createFieldDef(ModelFieldType.STRING),
    created_at: createFieldDef(ModelFieldType.STRING),
    updated_at: createFieldDef(ModelFieldType.STRING, null, true),
    notification_type: createFieldDef(ModelFieldType.STRING, NotificationType.REVIEWS),
    last_sent: createFieldDef(ModelFieldType.STRING),
    account_id: createFieldDef(ModelFieldType.STRING),
    flex: createFieldDef(ModelFieldType.OBJECT, null, true),
    email_addresses: createFieldDef(ModelFieldType.ARRAY),
    interval: createFieldDef(ModelFieldType.STRING, NotificationInterval.INSTANT),
};

const ignoreProps: (keyof NotificationFields)[] = [];

export default class Notification extends BaseModel<Props, NotificationFields> {
    static getByType = (accountId: string, notificationType: NotificationType,
                        callback: ModelResponse<Array<Notification>>) => {
        return getNotifications({
            account_id: accountId, notification_type: notificationType, callback: (data: ResponseData) => {
                const notifications: Array<Notification> = data.success ?
                    data.notifications.map((noti: any) => new Notification(noti)) : [];
                callback(notifications, data.success, data);
            }
        });
    }

    constructor(props?: Props) {
        super(ignoreProps, fieldsDefinition, fields, props);
        this.build(props);
    }

    get id(): string {
        return this.fields.id as string;
    }

    get notificationType(): NotificationType {
        return this.fields.notification_type;
    }

    get interval(): NotificationInterval {
        return this.fields.interval;
    }

    set interval(interval: NotificationInterval) {
        this.fields.interval = interval;
    }

    get emailAddresses(): Array<string> {
        return this.fields.email_addresses;
    }

    set emailAddresses(email_addresses: Array<string>) {
        this.fields.email_addresses = email_addresses;
    }

    get firstEmailAddress(): string | null {
        if(!this.fields.email_addresses.length) return null;
        return this.fields.email_addresses[0];
    }

    get lastSent(): string {
        return this.fields.last_sent;
    }

    set lastSent(last_sent: string) {
        this.fields.last_sent = last_sent;
    }

    get flex(): any | null {
        return this.fields.flex;
    }

    set flex(flex: any | null) {
        this.fields.flex = flex;
    }

    get accountId(): string {
        return this.fields.account_id;
    }

    set accountId(account_id: string) {
        this.fields.account_id = account_id;
    }

    create = (callback: ModelResponse<Notification>) => {
        return createNotification({
            data: {
                account_id: this.accountId, notification_type: this.notificationType, interval: this.interval,
                flex: this.flex, email_addresses: this.emailAddresses
            }, callback: (data: ResponseData) => {
                if (data.success) this.build(data.notification);
                callback(this, data.success, data);
            }
        });
    }

    update = (callback?: ModelResponse<Notification>) => {
        return updateNotification({
            data: {
                account_id: this.accountId, email_addresses: this.emailAddresses, flex: this.flex,
                notification_id: this.id, interval: this.interval
            }, callback: (data: ResponseData) => {
                if (data.success) this.build(data.notification);
                callback?.(this, data.success, data);
            }
        });
    }

    delete = (callback: ModelResponse<Notification>) => {
        deleteNotification({
            notification_id: this.id, account_id: this.accountId, callback: (data: ResponseData) => {
                callback(this, data.success, data);
            }
        })
    }

    protected build = (props?: Props) => {
        this.constructFields(props);
        if (props) {
            // TODO
        }
    };
}